import { Modal } from 'ui/dialog/modal/modal';
import h from 'hyperscript';
import { on } from 'cadenza/utils/event-util';
import { MIMETYPE_XLSX } from 'cadenza/api-client/api-constants';
import { GeometryType } from 'cadenza/map/geom/geometry-type';

import { LayerProperty } from '../layer-property';
import { escapeAndMarkHTML } from '../../utils/escape-and-mark';

import i18n from './layer-actions.properties';

import './export-layer-confirm-dialog.css';

const NAME = 'd-export-layer-confirm-dialog';

/**
 * @typedef ConfirmExportOptions
 * @property {string} title - Title for the dialog
 * @property {object} layer - Layer to export
 * @property {string} mimeType - MIME type to request (XLS, KML)
 * @property {boolean} showWarning - If true, display a warning about the high number of rows
 * @property {number} rowCount - Number of rows being exported
 */

/**
 * Show a confirmation dialog and return a promise of boolean that is fulfilled if the user confirms and rejected if he cancels.
 * The value of the promise will be true if the "include coordinates" checkbox was present and checked by the user.
 *
 * For Excel export the dialog has a checkbox where the user can specify if he wants the points' coordinates to be included.
 *
 * @param {ConfirmExportOptions} options
 * @return {Promise<boolean>} - true if "include coordinates" checkbox was present and checked by the user
 */
export function confirmExport (options) {
  const confirmDialog = new ExportLayerConfirmDialog(options);

  document.body.append(confirmDialog);

  confirmDialog.showModal();

  return new Promise((resolve, reject) => {
    on(confirmDialog, 'cancel', () => {
      reject();
      confirmDialog.remove();
    });

    on(confirmDialog, 'submit', () => {
      resolve(confirmDialog.includeCoordInExport);
      confirmDialog.remove();
    });
  });
}

export class ExportLayerConfirmDialog extends Modal {

  _layer;
  _mimeType;
  _includeCoordCheckbox;

  /**
   * Constructor
   *
   * @param {ConfirmExportOptions} options - Options object
   */
  constructor ({ title, layer, mimeType, warning, rowCount }) {
    super({ title });

    const message = escapeAndMarkHTML(
      layer.get(LayerProperty.SELECTED_FEATURE_IDS).length > 0
        ? i18n('layerExport.ensureDialog.hasSelectionMessage')
        : i18n('layerExport.ensureDialog.hasNoSelectionMessage')
    );

    const showIncludeCoordCheckbox = mimeType === MIMETYPE_XLSX
      && layer.get(LayerProperty.GEOMETRY_TYPE) === GeometryType.POINT;

    this.body.append(h('p', { innerHTML: message }));

    if (warning) {
      this.body.append(this._createWarning(rowCount));
    }

    if (showIncludeCoordCheckbox) {
      this.body.append(this._createIncludeCoordCheckbox());
    }

    this.addSubmitButton(i18n('layerExport.confirmDialog.continue'));
    this.addCancelButton(i18n('layerExport.confirmDialog.cancel'));
  }

  onSubmit (event) {
    this.close();
  }

  _createWarning (rowCount) {
    return h(
      '.d-alert.d-alert-warning',
      i18n('layerExport.errorDialog.rowCount.warning', { actualRows: rowCount })
    );
  }

  _createIncludeCoordCheckbox () {
    const checkbox = h('p.include-coord-checkbox', [
      this._includeCoordCheckbox = h('input', {
        type: 'checkbox',
        checked: false,
        id: 'include-coord-checkbox'
      }),
      h('label', { attrs: { for: 'include-coord-checkbox' } }, i18n('layerExport.include.coordinates'))
    ]);

    return checkbox;
  }

  get includeCoordInExport () {
    return this._includeCoordCheckbox && this._includeCoordCheckbox.checked;
  }

}

customElements.define(NAME, ExportLayerConfirmDialog);
